import React, { useCallback, useMemo, useState } from 'react';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    LabelList,
} from 'recharts';
import { Card, Select } from 'antd';
import determineStage from '@/utils/DetermineStage';
import { stageColors as defaultStageColors } from './Clicks/stageColors';
import {
    getCancellationDate,
    getCancellationMonth,
    getCancellationQuarter,
    getCancellationWeek,
    getCancellationYear,
    getQualitativeKPI,
    getStageActualMonth,
    getStageActualQuarter,
    getStageActualWeek,
    getStageActualYear,
    getStageOriginalPlanMonth,
    getStageOriginalPlanQuarter,
    getStageOriginalPlanWeek,
    getStageOriginalPlanYear,
    getStagePlanMonth,
    getStagePlanQuarter,
    getStagePlanWeek,
    getStagePlanYear,
    L0ActualDate,
    L1ActualDate,
    L1OriginalPlanDate,
    L2ActualDate,
    L2OriginalPlanDate,
    L2PlanDate,
    L3ActualDate,
    L3OriginalPlanDate,
    L3PlanDate,
    L4ActualDate,
    L4OriginalPlanDate,
    L4PlanDate,
    L5ActualDate,
    L5OriginalPlanDate,
    L5PlanDate,
    SubmittedForL1PlanDate,
    SubmittedForL2PlanDate,
    SubmittedForL3PlanDate,
    SubmittedForL4PlanDate,
    SubmittedForL5PlanDate
} from '@/utils/Sort/checkDate/getDate';
import checkWorkstreamLeadApprovalStatus from '@/utils/Sort/checkWorkstreamLeadApprovalStatus';
import checkWorkstreamSponsorApprovalStatus from '@/utils/Sort/checkWorkstreamSponsorApprovalStatus';
import {
    checkTOFinanceApprovalStatus,
    getComplianceStatusForGrouping,
    getNetRecurringBenefitCatagory,
    getNetRecurringBenefitCatagoryL0Plus,
    getNetRecurringBenefitCatagoryL1Plus,
    getNetRecurringBenefitCatagoryL2Plus,
    getNetRecurringBenefitCatagoryL3Plus,
    getNetRecurringBenefitCatagoryL4Plus,
    LLatestActualDate
} from '@/utils/helpers/helpers';
import checkStrategyAnalystApprovalStatus from '@/utils/Sort/checkStrategyAnalystApprovalStatus';
import InitiativePlanningSummary from '@/utils/initiativePlanningSummary';
import planningSummarySimplified from '@/utils/planningSummarySimplified';
import getImplementationCostCatagory from '@/utils/Sort/getImplementationCostCatagory';
import sortInitiativesByActualImpact from '@/utils/Sort/sortByActualImpact';
import sortInitiativesByActuals from '@/utils/Sort/sortByActual';
import Search from 'antd/lib/transfer/search';

// Helper function to safely sum values
const sumAnnualizedValue = (annualizedValue) => {
    if (typeof annualizedValue === 'number') {
        return annualizedValue;
    }
    if (Array.isArray(annualizedValue)) {
        return annualizedValue.reduce((sum, value) => sum + (value || 0), 0);
    }
    return 0;
};

// Custom Label component for the total value
const CustomTotalLabel = (props) => {
    const { x, y, width, value } = props;
    if (value === 0) return null;
    return (
        <text x={x + width + 5} y={y + 10} fill="#000" fontSize={12} textAnchor="start">
            {`Total: ${parseFloat(value).toFixed(3)}`}
        </text>
    );
};

const OneTimeBenefitChart = ({
    initiatives,
    initiativeColors,
    colorByAttribute,
    chartHeight = 1000,
    handleClick,
    selectedGroup
}) => {
    // Memoize the filtered initiatives to avoid re-processing on every render
    const [maxGroups, setMaxGroups] = useState(20);
    const [searchTerm, setSearchTerm] = useState('');
    const filteredInitiatives = useMemo(() =>
        initiatives.filter(i => !['On Hold', 'Cancelled', 'Rejected'].includes(i.weeklyStatus)&& i.segmentalPBT === true),
        [initiatives]
    );

    const getGroupKey = useCallback((initiative) => {
        if (!selectedGroup || selectedGroup === 'None') {
            // Default to initiative name if no group is selected
            return initiative.workstreamSponsorWorkstream?.[0]?.workstreamName || 'Unknown';
        }

        try {
            switch (selectedGroup) {
                case 'initiativeOwnerInfo':
                case 'workstreamLeadInfo':
                case 'workstreamSponsorInfo':
                case 'initiativeOwnerBackupInfo':
                case 'AllowOnlyTo':
                    const person = initiative[selectedGroup]?.[0];
                    return person ? `${person.firstName} ${person.lastName}` : 'Unknown';

                case 'workstreamSponsorWorkstream':
                    return initiative[selectedGroup]?.[0]?.workstreamName || 'Unknown';

                case 'workstreamLeadSubworkstream':
                    return initiative[selectedGroup]?.[0]?.subworkstreamName || 'Unknown';

                case 'stage':
                    return determineStage(initiative);

                case 'Stage (simplified)':
                    return initiative.stage || 'Unknown';

                case 'selectedForToMeetingDescusion':
                case 'hasFinancialImpact':
                    return initiative[selectedGroup] ? 'Yes' : 'No';

                case 'Has qualitative KPI?':
                    return getQualitativeKPI(initiative);

                case 'initiativeNumber - initiativeName':
                    return `${initiative.initiativeNumber || ''} - ${initiative.initiativeName || ''}`;

                case 'workstreamLeadPendingApproval':
                    return checkWorkstreamLeadApprovalStatus(initiative);

                case 'workstreamSponsorPendingApproval':
                    return checkWorkstreamSponsorApprovalStatus(initiative);

                case 'financeLeadPendingApproval':
                    return checkTOFinanceApprovalStatus(initiative);

                case 'Strategy analyst pending approval?':
                    return checkStrategyAnalystApprovalStatus(initiative);

                case 'planningSummary':
                    return InitiativePlanningSummary(initiative).planningStatus;

                case 'Planning summary (simplified)':
                    return planningSummarySimplified(initiative);

                case 'netRecurringBenefitsRange':
                    return getNetRecurringBenefitCatagory(initiative);

                case 'Future implementation costs range':
                    return getImplementationCostCatagory(initiative);

                case 'l2ForecastDate':
                    return L2PlanDate(initiative);

                case 'L3 date compliance check (forecast vs planned date)':
                    return getComplianceStatusForGrouping(initiative, 'L3');

                case 'L4 date compliance check (forecast vs planned date)':
                    return getComplianceStatusForGrouping(initiative, 'L4');

                case 'Number of Attachment':
                    return initiative.attachmentCount || 0;

                case 'actualImpactUpdateCheck':
                case 'actualKpiUpdateCheck':
                    return sortInitiativesByActualImpact(initiative, selectedGroup);

                case '(FY24-25) Actual impact update check':
                case '(FY23-24) Actual impact update check':
                    return sortInitiativesByActuals(initiative, selectedGroup);

                case 'cancellationDate':
                    return getCancellationDate(initiative);

                case 'l3PlannedDate':
                    return L3PlanDate(initiative);

                case 'l4PlannedDate':
                    return L4PlanDate(initiative);

                case 'l5LatestEstimatedDate':
                case 'l5ForecastDate':
                    return L5PlanDate(initiative);

                case 'l0ActualDate':
                    return L0ActualDate(initiative);

                case 'l1ActualDate':
                    return L1ActualDate(initiative);

                case 'l2ActualDate':
                    return L2ActualDate(initiative);

                case 'l3ActualDate':
                    return L3ActualDate(initiative);

                case 'l4ActualDate':
                    return L4ActualDate(initiative);

                case 'l5ActualDate':
                    return L5ActualDate(initiative);

                case 'latestActualDateAllLGates':
                    return LLatestActualDate(initiative);

                case 'subForL1ApprovDate':
                    return SubmittedForL1PlanDate(initiative);

                case 'subForL2ApprovDate':
                    return SubmittedForL2PlanDate(initiative);

                case 'subForL3ApprovDate':
                    return SubmittedForL3PlanDate(initiative);

                case 'subForL4ApprovDate':
                    return SubmittedForL4PlanDate(initiative);

                case 'subForL5ApprovDate':
                    return SubmittedForL5PlanDate(initiative);

                case 'subForL5ApprovDate_Year':
                    return getStageActualYear(initiative, 'Submitted for L5 Approval');

                case 'subForL5ApprovDate_Quarter':
                    return getStageActualQuarter(initiative, 'Submitted for L5 Approval');

                case 'subForL5ApprovDate_Month':
                    return getStageActualMonth(initiative, 'Submitted for L5 Approval');

                case 'subForL5ApprovDate_Week':
                    return getStageActualWeek(initiative, 'Submitted for L5 Approval');

                case 'pipelineWeeklyVarianceL0Plus':
                    return getNetRecurringBenefitCatagoryL0Plus(initiative);

                case 'pipelineWeeklyVarianceL1Plus':
                    return getNetRecurringBenefitCatagoryL1Plus(initiative);

                case 'pipelineWeeklyVarianceL2Plus':
                    return getNetRecurringBenefitCatagoryL2Plus(initiative);

                case 'pipelineWeeklyVarianceL3Plus':
                    return getNetRecurringBenefitCatagoryL3Plus(initiative);

                case 'pipelineWeeklyVarianceL4Plus':
                    return getNetRecurringBenefitCatagoryL4Plus(initiative);

                default:
                    // Handle date-based groupings with suffixes
                    if (selectedGroup.startsWith('l0ActualDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L0ActualDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'L0');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'L0');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'L0');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'L0');
                        }
                    } else if (selectedGroup.startsWith('l5LatestEstimatedDate') || selectedGroup.startsWith('l5ForecastDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L5PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStagePlanYear(initiative, 'L5');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStagePlanQuarter(initiative, 'L5');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStagePlanMonth(initiative, 'L5');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStagePlanWeek(initiative, 'L5');
                        }
                    } else if (selectedGroup.startsWith('l4PlannedDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L4PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStagePlanYear(initiative, 'L4');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStagePlanQuarter(initiative, 'L4');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStagePlanMonth(initiative, 'L4');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStagePlanWeek(initiative, 'L4');
                        }
                    } else if (selectedGroup.startsWith('l3PlannedDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L3PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStagePlanYear(initiative, 'L3');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStagePlanQuarter(initiative, 'L3');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStagePlanMonth(initiative, 'L3');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStagePlanWeek(initiative, 'L3');
                        }
                    } else if (selectedGroup.startsWith('l2ForecastDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L2PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStagePlanYear(initiative, 'L2');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStagePlanQuarter(initiative, 'L2');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStagePlanMonth(initiative, 'L2');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStagePlanWeek(initiative, 'L2');
                        }
                    } else if (selectedGroup.startsWith('l1ActualDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L1ActualDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'L1');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'L1');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'L1');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'L1');
                        }
                    } else if (selectedGroup.startsWith('l2ActualDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L2ActualDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'L2');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'L2');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'L2');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'L2');
                        }
                    } else if (selectedGroup.startsWith('l3ActualDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L3ActualDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'L3');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'L3');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'L3');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'L3');
                        }
                    } else if (selectedGroup.startsWith('latestActualDateAllLGates')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return LLatestActualDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'AllLGates');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'AllLGates');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'AllLGates');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'AllLGates');
                        }
                    } else if (selectedGroup.startsWith('l4ActualDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L4ActualDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'L4');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'L4');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'L4');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'L4');
                        }
                    } else if (selectedGroup?.startsWith('L1 Original Planned Date')) {
                        if (selectedGroup?.endsWith('_Day')) {
                            return L1OriginalPlanDate(initiative);
                        } else if (selectedGroup?.endsWith('_Year')) {
                            return getStageOriginalPlanYear(initiative, 'L1');
                        } else if (selectedGroup?.endsWith('_Quarter')) {
                            return getStageOriginalPlanQuarter(initiative, 'L1');
                        } else if (selectedGroup?.endsWith('_Month')) {
                            return getStageOriginalPlanMonth(initiative, 'L1');
                        } else if (selectedGroup?.endsWith('_Week')) {
                            return getStageOriginalPlanWeek(initiative, 'L1');
                        }
                    } else if (selectedGroup?.startsWith('L2 Original Planned Date')) {
                        if (selectedGroup?.endsWith('_Day')) {
                            return L2OriginalPlanDate(initiative);
                        } else if (selectedGroup?.endsWith('_Year')) {
                            return getStageOriginalPlanYear(initiative, 'L2');
                        } else if (selectedGroup?.endsWith('_Quarter')) {
                            return getStageOriginalPlanQuarter(initiative, 'L2');
                        } else if (selectedGroup?.endsWith('_Month')) {
                            return getStageOriginalPlanMonth(initiative, 'L2');
                        } else if (selectedGroup?.endsWith('_Week')) {
                            return getStageOriginalPlanWeek(initiative, 'L2');
                        }
                    } else if (selectedGroup?.startsWith('L3 Original Planned Date')) {
                        if (selectedGroup?.endsWith('_Day')) {
                            return L3OriginalPlanDate(initiative);
                        } else if (selectedGroup?.endsWith('_Year')) {
                            return getStageOriginalPlanYear(initiative, 'L3');
                        } else if (selectedGroup?.endsWith('_Quarter')) {
                            return getStageOriginalPlanQuarter(initiative, 'L3');
                        } else if (selectedGroup?.endsWith('_Month')) {
                            return getStageOriginalPlanMonth(initiative, 'L3');
                        } else if (selectedGroup?.endsWith('_Week')) {
                            return getStageOriginalPlanWeek(initiative, 'L3');
                        }
                    } else if (selectedGroup?.startsWith('L4 Original Planned Date')) {
                        if (selectedGroup?.endsWith('_Day')) {
                            return L4OriginalPlanDate(initiative);
                        } else if (selectedGroup?.endsWith('_Year')) {
                            return getStageOriginalPlanYear(initiative, 'L4');
                        } else if (selectedGroup?.endsWith('_Quarter')) {
                            return getStageOriginalPlanQuarter(initiative, 'L4');
                        } else if (selectedGroup?.endsWith('_Month')) {
                            return getStageOriginalPlanMonth(initiative, 'L4');
                        } else if (selectedGroup?.endsWith('_Week')) {
                            return getStageOriginalPlanWeek(initiative, 'L4');
                        }
                    } else if (selectedGroup?.startsWith('L5 Original Planned Date')) {
                        if (selectedGroup?.endsWith('_Day')) {
                            return L5OriginalPlanDate(initiative);
                        } else if (selectedGroup?.endsWith('_Year')) {
                            return getStageOriginalPlanYear(initiative, 'L5');
                        } else if (selectedGroup?.endsWith('_Quarter')) {
                            return getStageOriginalPlanQuarter(initiative, 'L5');
                        } else if (selectedGroup?.endsWith('_Month')) {
                            return getStageOriginalPlanMonth(initiative, 'L5');
                        } else if (selectedGroup?.endsWith('_Week')) {
                            return getStageOriginalPlanWeek(initiative, 'L5');
                        }
                    } else if (selectedGroup.startsWith('l5ActualDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return L5ActualDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'L5');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'L5');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'L5');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'L5');
                        }
                    } else if (selectedGroup.startsWith('subForL1ApprovDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return SubmittedForL1PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'Submitted for L1 Approval');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'Submitted for L1 Approval');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'Submitted for L1 Approval');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'Submitted for L1 Approval');
                        }
                    } else if (selectedGroup.startsWith('subForL2ApprovDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return SubmittedForL2PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'Submitted for L2 Approval');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'Submitted for L2 Approval');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'Submitted for L2 Approval');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'Submitted for L2 Approval');
                        }
                    } else if (selectedGroup.startsWith('subForL3ApprovDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return SubmittedForL3PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'Submitted for L3 Approval');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'Submitted for L3 Approval');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'Submitted for L3 Approval');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'Submitted for L3 Approval');
                        }
                    } else if (selectedGroup.startsWith('subForL4ApprovDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return SubmittedForL4PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'Submitted for L4 Approval');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'Submitted for L4 Approval');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'Submitted for L4 Approval');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'Submitted for L4 Approval');
                        }
                    } else if (selectedGroup.startsWith('subForL5ApprovDate')) {
                        if (selectedGroup.endsWith('_Day')) {
                            return SubmittedForL5PlanDate(initiative);
                        } else if (selectedGroup.endsWith('_Year')) {
                            return getStageActualYear(initiative, 'Submitted for L5 Approval');
                        } else if (selectedGroup.endsWith('_Quarter')) {
                            return getStageActualQuarter(initiative, 'Submitted for L5 Approval');
                        } else if (selectedGroup.endsWith('_Month')) {
                            return getStageActualMonth(initiative, 'Submitted for L5 Approval');
                        } else if (selectedGroup.endsWith('_Week')) {
                            return getStageActualWeek(initiative, 'Submitted for L5 Approval');
                        }
                    } else if (selectedGroup.startsWith('cancellationDate')) {
                        if (selectedGroup?.endsWith('_Day')) {
                            return getCancellationDate(initiative);
                        } else if (selectedGroup?.endsWith('_Year')) {
                            return getCancellationYear(initiative);
                        } else if (selectedGroup?.endsWith('_Quarter')) {
                            return getCancellationQuarter(initiative);
                        } else if (selectedGroup?.endsWith('_Month')) {
                            return getCancellationMonth(initiative);
                        } else if (selectedGroup?.endsWith('_Week')) {
                            return getCancellationWeek(initiative);
                        }
                    } else {
                        // Handle any other group selection by directly accessing the property
                        return initiative[selectedGroup] || 'Unknown';
                    }
            }
        } catch (error) {
            console.error(`Error getting group key for ${selectedGroup}:`, error);
            return 'Error';
        }

        // Default fallback
        return initiative[selectedGroup] || 'Unknown';
    }, [selectedGroup]);

    // Get all unique stages from filtered initiatives
    const allStages = useMemo(() => {
        const stages = new Set();
        filteredInitiatives.forEach(i => {
            if (i.stage) stages.add(i.stage);
        });
        return Array.from(stages).sort();
    }, [filteredInitiatives]);

    // Get all group keys from filtered initiatives
    const allGroupKeys = useMemo(() => {
        const keys = new Map(); // Use Map to track count for each key

        filteredInitiatives.forEach(initiative => {
            const key = getGroupKey(initiative);
            if (key) {
                keys.set(key, (keys.get(key) || 0) + 1);
            }
        });

        // Convert to array and sort by count (descending)
        return Array.from(keys.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([key]) => key);
    }, [filteredInitiatives, getGroupKey]);

    const filteredGroupKeys = useMemo(() => {
        let result = allGroupKeys;

        // Apply search filter
        if (searchTerm) {
            result = result.filter(key =>
                key.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Apply max groups limit
        return result.slice(0, maxGroups);
    }, [allGroupKeys, searchTerm, maxGroups]);

    // Calculate dynamic height based on number of groups
    const dynamicChartHeight = useMemo(() => {
        const baseHeight = 1000;
        const minHeight = 500;
        const maxHeight = 3000;
        const heightPerGroup = 40;

        // Calculate height based on number of groups
        let calculatedHeight = Math.max(minHeight, Math.min(maxHeight, filteredGroupKeys.length * heightPerGroup + 100));

        // If a fixed height is provided, use it, but ensure it's at least the calculated minimum
        return chartHeight ? Math.max(calculatedHeight, chartHeight) : calculatedHeight;
    }, [filteredGroupKeys.length, chartHeight]);

    const chartData = useMemo(() => {
        const oneTimeBenefitsData = {};

        // Initialize data structure only for filtered groups
        filteredGroupKeys.forEach(key => {
            oneTimeBenefitsData[key] = { total: 0 };
            allStages.forEach(stage => {
                oneTimeBenefitsData[key][stage] = 0;
            });
        });

        // Process initiatives
        filteredInitiatives.forEach(initiative => {
            const groupKey = getGroupKey(initiative);
            if (!groupKey || !oneTimeBenefitsData[groupKey]) return;

            const impacts = initiative.impact || [];
            const totalImpactForInitiative = impacts.reduce((sum, impact) => {
                if (impact.frequencyAndDirection?.includes('One Time Benefit')) {
                    const calendarizedPlanSum = sumAnnualizedValue(impact.annualizedValues?.plan);
                    return sum + calendarizedPlanSum;
                }
                return sum;
            }, 0);

            const stage = initiative.stage;
            if (stage && oneTimeBenefitsData[groupKey].hasOwnProperty(stage)) {
                oneTimeBenefitsData[groupKey][stage] += totalImpactForInitiative;
                oneTimeBenefitsData[groupKey].total += totalImpactForInitiative;
            }
        });

        // Convert to array format for Recharts
        return Object.keys(oneTimeBenefitsData)
            .sort((a, b) => oneTimeBenefitsData[b].total - oneTimeBenefitsData[a].total)
            .map(groupKey => {
                const groupData = oneTimeBenefitsData[groupKey];
                const newEntry = { groupKey };

                allStages.forEach(stage => {
                    const value = groupData[stage] || 0;
                    newEntry[stage] = value;
                });

                newEntry.total = groupData.total;
                return newEntry;
            });
    }, [filteredInitiatives, getGroupKey, allStages, filteredGroupKeys]);

    // Calculate min and max values for XAxis
    const { minX, maxX } = useMemo(() => {
        if (chartData.length === 0) return { minX: 0, maxX: 0 };

        const allValues = chartData.flatMap(d =>
            allStages.map(stage => d[stage] || 0)
        );
        return {
            minX: Math.min(0, ...allValues),
            maxX: Math.max(0, ...allValues)
        };
    }, [chartData, allStages]);

    const chartTitle = selectedGroup && selectedGroup !== 'None'
        ? `One Time Benefits by ${selectedGroup} (Calendarized, Planned, by Stage, ETB Mn)`
        : "One Time Benefits (Calendarized, Planned, by Stage, ETB Mn)";

    // Custom Tooltip component
    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;

        const groupData = chartData.find(d => d.groupKey === label);
        if (!groupData) return null;

        const tooltipData = allStages.map(stage => {
            const value = groupData[stage] || 0;
            return {
                stage,
                value
            };
        }).filter(item => item.value !== 0);

        if (tooltipData.length === 0) return null;

        return (
            <div className="custom-tooltip" style={{
                backgroundColor: '#fff',
                padding: '10px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                minWidth: '200px',
                maxWidth: '300px'
            }}>
                <p style={{ margin: 0, fontWeight: 'bold', marginBottom: '8px' }}>{label}</p>
                {tooltipData.map((item, index) => (
                    <div key={index} style={{
                        marginBottom: '4px',
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}>
                        <span style={{ marginRight: '10px' }}>{item.stage}:</span>
                        <span>{parseFloat(item.value).toFixed(3)} ETB Mn</span>
                    </div>
                ))}
                <div style={{
                    marginTop: '8px',
                    paddingTop: '8px',
                    borderTop: '1px solid #eee',
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontWeight: 'bold'
                }}>
                    <span>Total:</span>
                    <span>{parseFloat(groupData.total).toFixed(3)} ETB Mn</span>
                </div>
            </div>
        );
    };

    // Get colors based on colorByAttribute
    const colors = useMemo(() => {
        if (colorByAttribute === 'Stage (simplified)') {
            return initiativeColors;
        }
        return defaultStageColors;
    }, [colorByAttribute, initiativeColors]);

    // Custom Legend component
    const renderCustomLegend = (props) => {
        const { payload } = props;
        if (!payload || payload.length === 0) return null;

        return (
            <div style={{
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '5px'
            }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                    {allStages.map((stage, index) => {
                        const color = colors[stage] || '#ccc';
                        return (
                            <li
                                key={`item-${index}`}
                                style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}
                            >
                                <div
                                    style={{
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: color,
                                        marginRight: '5px',
                                        display: 'inline-block',
                                    }}
                                />
                                <span style={{ fontSize: '12px' }}>{stage}</span>
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
    };

    // If no data to display, show a message
    if (chartData.length === 0) {
        return (
            <Card
                title={<span style={{ color: '#0078D4' }} onClick={handleClick}>{chartTitle}</span>}
                style={{ width: '100%', cursor: 'pointer' }}
            >
                <div style={{
                    width: '100%',
                    height: 200,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#999'
                }}>
                    No data available for the selected filters
                </div>
            </Card>
        );
    }

    return (
        <Card
            title={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: '#0078D4', cursor: 'pointer' }} onClick={handleClick}>
                        {chartTitle}
                    </span>
                    {allGroupKeys.length > 20 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Search
                                placeholder="Search groups..."
                                allowClear
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ width: 200 }}
                            />
                            <Select
                                value={maxGroups}
                                onChange={setMaxGroups}
                                style={{ width: 120 }}
                            >
                                <Option value={10}>Top 10</Option>
                                <Option value={20}>Top 20</Option>
                                <Option value={50}>Top 50</Option>
                                <Option value={100}>Top 100</Option>
                                <Option value={0}>Show All</Option>
                            </Select>
                        </div>
                    )}
                </div>
            }
            style={{ width: '100%', cursor: 'pointer' }}
        >
            <div style={{ width: '100%', height: dynamicChartHeight }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        barCategoryGap="10%"
                        margin={{
                            top: 10,
                            right: 150,
                            left: 10,
                            bottom: 10
                        }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis
                            type="number"
                            domain={[minX * 1.1, maxX * 1.1]}
                            tickFormatter={(value) => parseFloat(value).toFixed(3)}
                            allowDecimals={false}
                        />
                        <YAxis
                            type="category"
                            dataKey="groupKey"
                            interval={0}
                            axisLine={false}
                            tickLine={false}
                            width={150}
                            tick={{
                                fill: '#000',
                                fontSize: 12,
                                textAnchor: 'end',
                                // Truncate long labels
                                formatter: (value) => {
                                    if (value.length > 30) {
                                        return value.substring(0, 27) + '...';
                                    }
                                    return value;
                                }
                            }}
                            allowDataOverflow={true}
                        />
                        <Tooltip
                            content={<CustomTooltip />}
                            cursor={{ fill: 'rgba(0, 0, 0, 0.1)' }}
                        />
                        <Legend
                            layout="vertical"
                            verticalAlign="middle"
                            align="right"
                            content={renderCustomLegend}
                            wrapperStyle={{
                                right: 10,
                                top: 50,
                                backgroundColor: '#f9f9f9',
                                border: '1px solid #d9d9d9',
                                borderRadius: '4px',
                                padding: '10px'
                            }}
                        />
                        {allStages.map(stage => (
                            <Bar
                                key={stage}
                                dataKey={stage}
                                stackId="stack"
                                fill={colors[stage]}
                                isAnimationActive={false}
                                name={stage}
                            >
                                <LabelList
                                    dataKey={stage}
                                    position="insideRight"
                                    formatter={(value) => value !== 0 ? parseFloat(value).toFixed(3) : ''}
                                    fill="#fff"
                                    fontSize={10}
                                />
                            </Bar>
                        ))}
                        <LabelList dataKey="total" content={<CustomTotalLabel />} />
                    </BarChart>
                </ResponsiveContainer>
            </div>
            {allGroupKeys.length > maxGroups && maxGroups > 0 && (
                <div style={{
                    marginTop: '10px',
                    textAlign: 'center',
                    color: '#666',
                    fontSize: '12px'
                }}>
                    Showing {filteredGroupKeys.length} of {allGroupKeys.length} groups.
                    Use search and filters above to see more.
                </div>
            )}
        </Card>
    );
};

export default OneTimeBenefitChart;